/**
 * JWT 토큰 기반 인증 유틸리티
 */

/**
 * @typedef {Object} ApiResponse
 * @property {*} data - 응답 데이터
 * @property {string|null} message - 메시지
 * @property {'SUCCESS'|'FAIL'} result - 결과
 * @property {number} statusCode - HTTP 상태 코드
 * @property {string|null} statusMessage - 상태 메시지
 */

const TOKEN_KEY = 'token';
const USER_KEY = 'user';

/**
 * 로그인 성공 시 인증 정보 저장
 * @param {Object} user - 사용자 정보
 * @param {string} token - JWT 토큰
 */
export const saveAuth = (user, token) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * localStorage에서 토큰 반환
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 토큰 존재 여부 확인 (만료 검증은 서버에서 처리)
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * 로그아웃 처리: localStorage 클리어 + 로그인 페이지로 이동
 */
export const logout = () => {
  localStorage.clear();
  window.location.href = '/login';
};

/**
 * API 에러 클래스
 */
export class ApiError extends Error {
  /**
   * @param {string} message - 에러 메시지
   * @param {number} statusCode - 상태 코드
   * @param {string|null} statusMessage - 상태 메시지
   */
  constructor(message, statusCode, statusMessage) {
    super(message);
    this.name = 'ApiError';
    /** @type {number} */
    this.statusCode = statusCode;
    /** @type {string|null} */
    this.statusMessage = statusMessage;
  }
}

/**
 * axios 인스턴스에 인증 및 통합 응답 처리 interceptor 설정
 *
 * - Request: Authorization 헤더 자동 첨부
 * - Response 성공: 통합 응답 구조에서 data 필드만 언래핑하여 반환
 * - Response 실패: ApiError로 변환, 401 시 자동 로그아웃
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 * @returns {import('axios').AxiosInstance}
 */
export const setupAuthInterceptors = (axiosInstance) => {
  // Request interceptor: Authorization 헤더 자동 첨부
  axiosInstance.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor: 통합 응답 구조 처리
  axiosInstance.interceptors.response.use(
    (response) => {
      /** @type {ApiResponse} */
      const body = response.data;

      // 통합 응답 구조가 아닌 경우 그대로 반환
      if (body.result === undefined) return response;

      // result === "FAIL" 처리
      if (body.result === 'FAIL') {
        if (body.statusCode === 401) {
          logout();
        }
        return Promise.reject(
          new ApiError(
            body.message || '요청에 실패했습니다.',
            body.statusCode,
            body.statusMessage
          )
        );
      }

      // 성공: data 필드만 언래핑
      response.data = body.data;
      return response;
    },
    (error) => {
      if (error.response) {
        /** @type {ApiResponse} */
        const body = error.response.data;

        // 통합 응답 구조인 경우 메시지 활용
        if (body && body.result === 'FAIL') {
          if (body.statusCode === 401) {
            logout();
          }
          return Promise.reject(
            new ApiError(
              body.message || '요청에 실패했습니다.',
              body.statusCode,
              body.statusMessage
            )
          );
        }

        // 통합 구조가 아닌 401
        if (error.response.status === 401) {
          logout();
        }
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
